import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, DatePicker, Form, Input, Modal, Radio, Select, Typography, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useApiClient from '../../hooks/api/useApiClient';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import useHelpers from '../../hooks/context/useHelpers';
import { Utils } from '../../services/helpers/Utils';
import { DocumentHolder } from '../../services/exports/Constants';
import { useReplaceBusinessDocumentMutation, useUploadSlotDocumentMutation } from '../../store/api-slices';

const ACCEPTED_TYPES = 'image/png,image/jpeg,application/pdf';

// Some pickers (Android's among them) send no type or an unusual one (`application/x-pdf`) for a
// file the backend takes by its extension, so the extension decides when the type does not.
const ACCEPTED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf'];
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg'];
// The file picker's filter: by type and by extension, so the picker does not grey out such a file.
const PICKER_ACCEPT = [ACCEPTED_TYPES, ...ACCEPTED_EXTENSIONS.map((extension) => `.${extension}`)].join(',');

const extensionOf = (file) => (file.name?.includes('.') ? file.name.split('.').pop().toLowerCase() : '');
const isAcceptedFile = (file) =>
  ACCEPTED_TYPES.split(',').includes(file.type) || ACCEPTED_EXTENSIONS.includes(extensionOf(file));
const isImageFile = (file) =>
  file.type ? file.type.startsWith('image/') : IMAGE_EXTENSIONS.includes(extensionOf(file));

const DEFAULT_ID_KIND = 'id_card';

// What a slot takes when the backend did not say: its general limit.
const DEFAULT_MAX_KILOBYTES = 10240;

// The backend takes an ID only if it is still valid in a month and a day.
const earliestExpiryDate = () => dayjs().add(1, 'month').add(1, 'day').startOf('day');

/**
 * Adds a file to a document slot, or replaces one of its files (`replacing`) with a new version.
 * An ID takes a front, its back — required for an ID card, which Adyen takes only with both sides
 * and Stripe asks for too (Bohdan) — which kind of ID it is, its expiry date and, for a new file,
 * whose ID it is; every other slot takes one file per upload. A replacement keeps whose ID it is:
 * it is the same person's. A file is checked against what the slot takes (type, size, and for a
 * slot whose files go to Adyen, Adyen's own limits) before it is accepted.
 */
export default function DocumentSlotUploadModal({ slot, slotLabel, replacing = null, onClose }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:DocumentSlots:DocumentSlotUploadModal' });
  const { dateInputFormat } = useHelpers();

  const { BusinessProfilesManager } = useApiClient();
  const [uploadSlotDocument, { isLoading: uploading }] = useUploadSlotDocumentMutation();
  const [replaceDocument, { isLoading: replacingFile }] = useReplaceBusinessDocumentMutation();
  const { setFeedback, renderFeedbackLabel } = useFeedbackLabel();

  const isIdentity = Boolean(slot?.is_identity);
  const earliestExpiry = earliestExpiryDate();

  // A replacement keeps its kind when the slot still takes it; an ID from the old step can be a
  // kind the slot no longer offers (a driving licence), which the backend would refuse.
  const [kind, setKind] = useState(
    replacing?.kind && (slot?.kinds ?? []).includes(replacing.kind) ? replacing.kind : DEFAULT_ID_KIND,
  );
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [holder, setHolder] = useState(DocumentHolder.Owner);
  const [holderName, setHolderName] = useState('');
  // Why the last file picked for a field was refused, shown under that field: a toast went
  // unnoticed behind the dialog, and the file simply did not appear (QA, PROD-713).
  const [refusals, setRefusals] = useState({});

  const asksHolder = isIdentity && !replacing;
  const isPartner = asksHolder && holder === DocumentHolder.Partner;
  const needsBack = isIdentity && kind === DEFAULT_ID_KIND;
  const canSubmit =
    Boolean(front) &&
    (!needsBack || Boolean(back)) &&
    (!isIdentity || Boolean(expiresAt)) &&
    (!isPartner || holderName.trim() !== '');

  const maxKilobytes = slot?.max_kilobytes ?? DEFAULT_MAX_KILOBYTES;
  const minImageKilobytes = slot?.min_image_kilobytes ?? null;
  const maxMegabytes = Math.round(maxKilobytes / 1024);

  const refusal = (file) => {
    if (!isAcceptedFile(file)) {
      return t('errors.file_type');
    }

    if (file.size > maxKilobytes * 1024) {
      return t('errors.file_too_large', { max: maxMegabytes });
    }

    if (minImageKilobytes && isImageFile(file) && file.size < minImageKilobytes * 1024) {
      return t('errors.file_too_small', { min: minImageKilobytes });
    }

    return null;
  };

  const submit = async () => {
    const data = Utils.createFormData({
      front,
      ...(isIdentity && { kind, back: back ?? null, expires_at: expiresAt }),
      ...(asksHolder && { holder }),
      ...(isPartner && { holder_name: holderName.trim() }),
    });

    const { error } = replacing
      ? await replaceDocument({ apiManager: BusinessProfilesManager, id: replacing.id, data })
      : await uploadSlotDocument({ apiManager: BusinessProfilesManager, slot: slot.slot, data });

    if (error) {
      setFeedback({ message: error?.message, type: 'error', errors: error?.errors });

      return toast.error(t('toasts.error'));
    }

    toast.success(t('toasts.success'));

    return onClose();
  };

  const renderFileField = (name, label, file, setFile, required) => (
    <Form.Item
      label={label}
      required={required}
      validateStatus={refusals[name] ? 'error' : undefined}
      help={refusals[name]}
    >
      <Upload
        listType="picture"
        multiple={false}
        maxCount={1}
        accept={PICKER_ACCEPT}
        fileList={file ? [file] : []}
        beforeUpload={(selected) => {
          const refused = refusal(selected);

          setRefusals((current) => ({ ...current, [name]: refused }));

          if (refused) {
            return Upload.LIST_IGNORE;
          }

          setFile(selected);

          return false;
        }}
        onRemove={() => setFile(null)}
      >
        <Button icon={<UploadOutlined />} data-testid={`document-slot-${name}`}>
          {t('buttons.select_file')}
        </Button>
      </Upload>
    </Form.Item>
  );

  const renderIdentityFields = () => (
    <>
      <Form.Item label={t('form.kind.label')} required>
        <Select
          value={kind}
          onChange={setKind}
          options={(slot?.kinds ?? []).map((option) => ({ value: option, label: t(`form.kind.options.${option}`) }))}
        />
      </Form.Item>
      {renderFileField('front', t('form.front.label'), front, setFront, true)}
      {renderFileField('back', t('form.back.label'), back, setBack, needsBack)}
      <Form.Item label={t('form.expires_at.label')} required>
        <DatePicker
          className="w-full"
          format={[dateInputFormat, 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY']}
          value={expiresAt ? dayjs(expiresAt) : null}
          placeholder={t('form.expires_at.placeholder')}
          disabledDate={(current) => current && current < earliestExpiry}
          onChange={(value) => setExpiresAt(value ? value.utc(true).toISOString() : null)}
        />
      </Form.Item>
      {asksHolder && (
        <Form.Item label={t('form.holder.label')} required>
          <Radio.Group value={holder} onChange={(event) => setHolder(event.target.value)}>
            <Radio value={DocumentHolder.Owner}>{t('form.holder.options.owner')}</Radio>
            <Radio value={DocumentHolder.Partner}>{t('form.holder.options.partner')}</Radio>
          </Radio.Group>
        </Form.Item>
      )}
      {isPartner && (
        <Form.Item label={t('form.holder_name.label')} required>
          <Input
            value={holderName}
            placeholder={t('form.holder_name.placeholder')}
            onChange={(event) => setHolderName(event.target.value)}
          />
        </Form.Item>
      )}
    </>
  );

  return (
    <Modal
      open
      onCancel={onClose}
      title={t(replacing ? 'replace_title' : 'title', { slot: slotLabel })}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('buttons.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={submit}
          loading={uploading || replacingFile}
          disabled={!canSubmit}
          data-testid="document-slot-submit"
        >
          {t('buttons.submit')}
        </Button>,
      ]}
    >
      {replacing && <Typography.Paragraph>{t('replace_description')}</Typography.Paragraph>}
      {renderFeedbackLabel}
      <Form layout="vertical">
        {isIdentity ? renderIdentityFields() : renderFileField('front', t('form.file.label'), front, setFront, true)}
      </Form>
      <Typography.Text type="secondary" className="text-xs">
        {t('accepted_files', { max: maxMegabytes })}
        {minImageKilobytes ? ` ${t('accepted_images_min', { min: minImageKilobytes })}` : ''}
      </Typography.Text>
    </Modal>
  );
}
